import { FormResponse } from "@/models/forms";
import { formatAmount, formatDate } from "@/util";
import { FC } from "react";
import { useTranslation } from "react-i18next";

interface IFormsFunDayViewProps {
    formData: FormResponse | null;
}

const FormsFunDayModalContent: FC<IFormsFunDayViewProps> = (props: IFormsFunDayViewProps): JSX.Element => {
    const { t } = useTranslation();

    function getRegionsInfo(
        regionOne: string | undefined,
        regionTwo: string | undefined,
        regionThree: string | undefined
    ): string {
        const regionDataArray = [regionOne, regionTwo, regionThree];
        const regionsArray = regionDataArray.filter((region) => region !== "").join(", ");

        return regionsArray;
    }

    function handleFormYesOrNoProp(property: boolean | undefined) {
        if (property) {
            return t("forms.Yes");
        } else {
            return t("forms.No");
        }
    }

    function getAlternativeHolidayDatesInfo(dateOne: string, dateTwo: string, dateThree: string): string {
        var datesArray = [dateOne, dateTwo, dateThree];
        var filteredDates = datesArray
            .filter((date) => date !== "")
            .map((date) => formatDate(date))
            .join(", ");

        return filteredDates;
    }

    function getFunDayEnumsData(enumTypes: string): string {
        const typesArray = enumTypes
            .split(", ")
            .map((type) => t(`forms.CreateFunDay.${type}`))
            .join(", ");

        return typesArray;
    }

    return (
        <div style={{ display: "grid", gap: "20px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                <div className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.ClientName")}</div>
                            <div className="font-style-bold-16">{props.formData?.clientName}</div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.Mobile")}</div>
                            <div className="font-style-bold-16">{props.formData?.mobile}</div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.Email")}</div>
                            <div className="font-style-bold-16">{props.formData?.email}</div>
                        </div>
                    </div>
                </div>
                <div className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.FunDayGoal")}</div>
                            <div className="font-style-bold-16">{props.formData?.formGoal}</div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.ParticipantsNumber")}</div>
                            <div className="font-style-bold-16">
                                {props.formData?.numberOfParticipants?.toLocaleString("en-US")}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.DesiredRegions")}</div>
                            <div className="font-style-bold-16">
                                {getRegionsInfo(
                                    props.formData?.desiredRegionOne ?? "",
                                    props.formData?.desiredRegionTwo ?? "",
                                    props.formData?.desiredRegionThree ?? ""
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                <div className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.BudgetPerEmployee")}</div>
                            <div className="font-style-bold-16">
                                {formatAmount(props.formData?.budgetPerEmployee, props.formData?.currencyId)}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.FamilyBudget")}</div>
                            <div className="font-style-bold-16">
                                {formatAmount(props.formData?.familyBudget, props.formData?.currencyId)}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.BudgetForCouple")}</div>
                            <div className="font-style-bold-16">
                                {formatAmount(props.formData?.budgetForCouple, props.formData?.currencyId)}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.Comments")}</div>
                            <div className="font-style-bold-16">{props.formData?.budgetComments}</div>
                        </div>
                    </div>
                </div>
                <div className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.RequestedFrom")}</div>
                            <div className="font-style-bold-16">{props.formData?.requestedFrom}</div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.RequestedTo")}</div>
                            <div className="font-style-bold-16">{props.formData?.requestedTo}</div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.AlternativeHolidayDates")}</div>
                            <div className="font-style-bold-16">
                                {getAlternativeHolidayDatesInfo(
                                    props.formData?.alternativeHolidayDateOne ?? "",
                                    props.formData?.alternativeHolidayDateTwo ?? "",
                                    props.formData?.alternativeHolidayDateThree ?? ""
                                )}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.Comments")}</div>
                            <div className="font-style-bold-16">{props.formData?.alternativeHolidayDateComments}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                <div className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.Visitors")}</div>
                            <div className="font-style-bold-16">
                                {t(`forms.CreateFunDay.${props.formData?.visitors}`)}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.NeedBus")}</div>
                            <div className="font-style-bold-16">{handleFormYesOrNoProp(props.formData?.needBus)}</div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.NeedGuide")}</div>
                            <div className="font-style-bold-16">{handleFormYesOrNoProp(props.formData?.needGuide)}</div>
                        </div>
                    </div>
                </div>
                <div className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.Nature")}</div>
                            <div className="font-style-bold-16">
                                {getFunDayEnumsData(props.formData?.natureFunDayType?.toString() ?? "")}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.Comments")}</div>
                            <div className="font-style-bold-16">{props.formData?.natureFunDayComments}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", paddingBottom: "40px" }}>
                <div className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.InterestedIn")}</div>
                            <div className="font-style-bold-16">
                                {getFunDayEnumsData(props.formData?.mealType?.toString() ?? "")}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.Comments")}</div>
                            <div className="font-style-bold-16">{props.formData?.mealComments}</div>
                        </div>
                    </div>
                </div>
                <div className="forms-view-content-data">
                    <div>
                        <div style={{ padding: "13px 10px 14px 15px" }}>
                            <div className="font-style">{t("forms.FunDay.WantToIncorporate")}</div>
                            <div style={{ paddingTop: 7 }} className="font-style-bold-16">
                                {handleFormYesOrNoProp(props.formData?.wantIncorporateLectureRecognition)}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.BudgetForCouple")}</div>
                            <div className="font-style-bold-16">
                                {formatAmount(
                                    props.formData?.incorporateLectureRecognitionBudget ?? 0,
                                    props.formData?.currencyId
                                )}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.Comments")}</div>
                            <div className="font-style-bold-16">
                                {props.formData?.incorporateLectureRecognitionComments}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FormsFunDayModalContent;
